import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { DWSLanguagesService } from '@services/dws/dws-languages.service';

type FlagSize = 'small' | 'medium' | 'large';
type FlagFormat = 'svg' | 'png' | 'webp';

@Component({
  selector: 'ws-flag',
  templateUrl: './ws-flag.component.html',
  styleUrls: ['./ws-flag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WsFlagComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() countryCode = '';
  @Input() size: FlagSize = 'medium';
  @Input() showBorder = false;
  @Input() alt = '';
  @Input() defer = true; // load only when in/near viewport
  @Input() preferFormat: FlagFormat = 'svg';
  @Input() fetchPriority: 'high' | 'low' | 'auto' = 'low';

  // bindable src (direct URL)
  src: string | null = null;

  private io?: IntersectionObserver;
  private canLoad = !this.defer;
  private lastKey = '';

  private sizes: Record<FlagSize, { w: number; h: number }> = {
    small: { w: 16, h: 12 },
    medium: { w: 24, h: 18 },
    large: { w: 36, h: 27 },
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private dwsLanguagesService: DWSLanguagesService
  ) {}

  ngAfterViewInit(): void {
    if (!this.defer) {
      this.tryLoad();
      return;
    }
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              this.canLoad = true;
              this.io?.disconnect();
              this.tryLoad();
              break;
            }
          }
        },
        { rootMargin: '256px' }
      );
      this.io.observe(this.host.nativeElement);
    } else {
      // Fallback: just allow load; native loading="lazy" will still help
      this.canLoad = true;
      this.tryLoad();
    }
    
    // Also try to load immediately if the element is already visible
    if (this.isElementVisible()) {
      this.canLoad = true;
      this.tryLoad();
    }
  }

  ngOnChanges(_: SimpleChanges): void {
    // If inputs that affect URL change, reload
    const key = this.buildKey();
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.src = null;
      this.cdr.markForCheck();
      this.tryLoad();
    }
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
  }

  get width(): number {
    return this.sizes[this.size].w;
  }
  get height(): number {
    return this.sizes[this.size].h;
  }
  get altText(): string {
    return this.alt || (this.countryCode ? `${this.countryCode} flag` : 'flag');
  }

  get safeUrl(): string {
    if (!this.countryCode || typeof this.countryCode !== 'string') {
      return '';
    }
    const code = this.dwsLanguagesService.getCountryCodeForLanguage(this.countryCode.toLowerCase());
    return `https://flagcdn.com/${code}.svg`;
  }

  private isElementVisible(): boolean {
    const rect = this.host.nativeElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private buildKey(): string {
    if (!this.countryCode || typeof this.countryCode !== 'string') {
      return ['', this.size].join('|');
    }
    const code = this.countryCode.toLowerCase();
    return [code, this.size].join('|');
  }

  private buildUrl(): string | null {
    if (!this.countryCode || typeof this.countryCode !== 'string') return null;
    const code = this.dwsLanguagesService.getCountryCodeForLanguage(this.countryCode.toLowerCase().trim());
    
    // Validate country code format (2-3 letters)
    if (!/^[a-z]{2,3}$/.test(code)) return null;
    
    // Always use SVG for better reliability and performance
    return `https://flagcdn.com/${code}.svg`;
  }

  private tryLoad(): void {
    if (!this.canLoad || !this.countryCode || typeof this.countryCode !== 'string') return;

    const url = this.buildUrl();
    if (!url) return;

    setTimeout(() => {
      this.src = url;
      this.cdr.markForCheck();
    });
  }
}
